import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { QrCode, Plus, CheckCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const ScannerQR = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [formData, setFormData] = useState({ numero_furgon: '', nombre_sesion: '' });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await api.get('/scan-sessions');
      setSessions(response.data || []);
    } catch (error) {
      toast.error('Error al cargar sesiones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/scan-sessions', {
        ...formData,
        fecha_inicio: new Date().toISOString(),
        estado: 'en_progreso'
      });
      toast.success('Sesión creada');
      setActiveSession(response.data);
      fetchSessions();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Error al crear sesión');
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!activeSession) {
      toast.error('Selecciona una sesión primero');
      return;
    }

    try {
      const response = await api.post(`/scan-sessions/${activeSession.id}/scan`, { codigo_qr: scanInput });
      if (response.data.duplicate) {
        toast.warning('Artículo ya escaneado');
      } else {
        toast.success('Artículo escaneado');
        setActiveSession(prev => ({
          ...prev,
          total_escaneados: response.data.progreso.escaneados
        }));
      }
      setScanInput('');
    } catch (error) {
      toast.error('Error al escanear');
    }
  };

  const getProgreso = (session) => {
    if (session.total_items_furgon === 0) return 0;
    return (session.total_escaneados / session.total_items_furgon) * 100;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Scanner Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-2xl flex items-center"><QrCode className="mr-2" />Escaner QR</CardTitle>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Nueva Sesión</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Nueva Sesión de Escaneo</DialogTitle></DialogHeader>
                    <form onSubmit={handleCreateSession} className="space-y-4">
                      <div><Label>Número de Furgón *</Label><Input value={formData.numero_furgon} onChange={(e) => setFormData({...formData, numero_furgon: e.target.value})} required /></div>
                      <div><Label>Nombre de Sesión</Label><Input value={formData.nombre_sesion} onChange={(e) => setFormData({...formData, nombre_sesion: e.target.value})} placeholder="Sesión 1" /></div>
                      <div className="flex justify-end space-x-2">
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                        <Button type="submit">Crear</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {activeSession ? (
                <div className="space-y-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-600">Sesión Activa</p>
                    <p className="text-xl font-bold text-blue-600">{activeSession.numero_furgon}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progreso</span>
                      <span className="font-medium">{activeSession.total_escaneados} / {activeSession.total_items_furgon}</span>
                    </div>
                    <Progress value={getProgreso(activeSession)} className="h-3" />
                    <p className="text-xs text-gray-500 text-right">{getProgreso(activeSession).toFixed(1)}% completado</p>
                  </div>

                  <form onSubmit={handleScan} className="space-y-3">
                    <div>
                      <Label>Código QR</Label>
                      <Input
                        value={scanInput}
                        onChange={(e) => setScanInput(e.target.value.toUpperCase())}
                        placeholder="MF1234-ACEITE-1"
                        autoFocus
                      />
                    </div>
                    <Button type="submit" className="w-full"><CheckCircle className="mr-2 h-4 w-4" />Escanear</Button>
                  </form>

                  <Button variant="outline" className="w-full" onClick={() => setActiveSession(null)}>Cambiar Sesión</Button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <QrCode className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Selecciona o crea una sesión para comenzar</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sessions List */}
          <Card>
            <CardHeader><CardTitle>Sesiones de Escaneo</CardTitle></CardHeader>
            <CardContent>
              {loading ? <div className="text-center py-8">Cargando...</div> : (
                <div className="space-y-3">
                  {sessions.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No hay sesiones</p>
                  ) : (
                    sessions.map((session) => (
                      <div key={session.id} className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={() => setActiveSession(session)}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{session.numero_furgon}</p>
                          <Badge>{session.estado}</Badge>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>{session.nombre_sesion || 'Sin nombre'}</p>
                          <p className="text-xs mt-1">{session.total_escaneados} / {session.total_items_furgon} artículos</p>
                        </div>
                        <Progress value={getProgreso(session)} className="h-2 mt-2" />
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScannerQR;
